/**
 * The log search grammar. Small on purpose, so people can learn it from the placeholder:
 *
 *   service:api level:error status:5xx path:/api -healthz "connection refused"
 *
 *   service:<name>     (also svc:)      only this service
 *   level:<level>      (also lvl:)      this level and worse: debug, info, warn, error, fatal
 *   source:<source>    (also src:)      http, runtime, build, deploy, platform
 *   status:<code>                       HTTP status: 404, 5xx, >=400, 2xx
 *   path:<prefix>                       HTTP path starts with this
 *   method:<verb>                       GET, POST, …
 *   -word / -"phrase"                   leave out lines containing this
 *   word / "a phrase"                   lines must contain all of these (case-insensitive)
 *
 * Service, level, source and the longest text term are sent to the API; the rest is applied in the browser.
 */
import type { LogLine } from "@/lib/api";

export const LEVELS = ["debug", "info", "warn", "error", "fatal"] as const;
export type Level = (typeof LEVELS)[number];
export const SOURCES = ["http", "runtime", "build", "deploy", "platform"] as const;
export type Source = (typeof SOURCES)[number];

export type TokenKind = "service" | "level" | "source" | "status" | "path" | "method" | "text" | "exclude" | "invalid";

export interface Token {
  kind: TokenKind;
  /** The value without the key, unquoted. */
  value: string;
  /** Exactly as typed (used to rebuild and remove tokens). */
  raw: string;
  /** Why an invalid token was ignored. */
  error?: string;
}

export interface StatusMatcher {
  test: (status: number) => boolean;
  label: string;
}

export interface ParsedQuery {
  tokens: Token[];
  service?: string;
  level?: Level;
  source?: Source;
  status: StatusMatcher[];
  path?: string;
  method?: string;
  text: string[];
  exclude: string[];
  errors: string[];
}

const KEY_ALIASES: Record<string, Exclude<TokenKind, "text" | "exclude" | "invalid">> = {
  service: "service",
  svc: "service",
  level: "level",
  lvl: "level",
  source: "source",
  src: "source",
  status: "status",
  path: "path",
  method: "method",
};

/** Split on spaces, keeping "quoted phrases" (with an optional key: or - prefix) together. */
export function tokenise(input: string): string[] {
  const out: string[] = [];
  const re = /(-?(?:[a-z]+:)?"[^"]*"?)|(\S+)/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(input))) out.push(m[0]);
  return out;
}

const unquote = (s: string) => s.replace(/^"/, "").replace(/"$/, "");

function parseStatus(v: string): StatusMatcher | null {
  const s = v.toLowerCase();
  let m = /^([1-5])xx$/.exec(s);
  if (m) {
    const d = Number(m[1]);
    return { label: `${d}xx`, test: (n) => Math.floor(n / 100) === d };
  }
  m = /^(>=|<=|>|<)?(\d{3})$/.exec(s);
  if (m) {
    const n0 = Number(m[2]);
    const op = m[1] ?? "=";
    const test = op === ">=" ? (n: number) => n >= n0 : op === "<=" ? (n: number) => n <= n0 : op === ">" ? (n: number) => n > n0 : op === "<" ? (n: number) => n < n0 : (n: number) => n === n0;
    return { label: `${op === "=" ? "" : op}${n0}`, test };
  }
  return null;
}

export function parseQuery(input: string): ParsedQuery {
  const q: ParsedQuery = { tokens: [], status: [], text: [], exclude: [], errors: [] };
  for (const raw of tokenise(input)) {
    const kv = /^([a-z]+):(.*)$/i.exec(raw);
    const key = kv ? KEY_ALIASES[kv[1]!.toLowerCase()] : undefined;
    if (kv && key) {
      const value = unquote(kv[2] ?? "");
      if (!value) {
        q.tokens.push({ kind: "invalid", value: raw, raw, error: `Add a value after ${kv[1]}:` });
        continue;
      }
      if (key === "level") {
        const lv = value.toLowerCase() === "warning" ? "warn" : value.toLowerCase();
        if (!(LEVELS as readonly string[]).includes(lv)) {
          const error = `“${value}” is not a level. Use ${LEVELS.join(", ")}.`;
          q.tokens.push({ kind: "invalid", value, raw, error });
          q.errors.push(error);
          continue;
        }
        q.level = lv as Level;
        q.tokens.push({ kind: "level", value: lv, raw });
      } else if (key === "source") {
        const sv = value.toLowerCase();
        if (!(SOURCES as readonly string[]).includes(sv)) {
          const error = `“${value}” is not a source. Use ${SOURCES.join(", ")}.`;
          q.tokens.push({ kind: "invalid", value, raw, error });
          q.errors.push(error);
          continue;
        }
        q.source = sv as Source;
        q.tokens.push({ kind: "source", value: sv, raw });
      } else if (key === "status") {
        const sm = parseStatus(value);
        if (!sm) {
          const error = `“${value}” is not a status. Try 500, 5xx or >=400.`;
          q.tokens.push({ kind: "invalid", value, raw, error });
          q.errors.push(error);
          continue;
        }
        q.status.push(sm);
        q.tokens.push({ kind: "status", value: sm.label, raw });
      } else if (key === "service") {
        q.service = value;
        q.tokens.push({ kind: "service", value, raw });
      } else if (key === "path") {
        q.path = value;
        q.tokens.push({ kind: "path", value, raw });
      } else if (key === "method") {
        q.method = value.toUpperCase();
        q.tokens.push({ kind: "method", value: value.toUpperCase(), raw });
      }
      continue;
    }
    if (raw.startsWith("-") && raw.length > 1) {
      const value = unquote(raw.slice(1));
      if (value) {
        q.exclude.push(value.toLowerCase());
        q.tokens.push({ kind: "exclude", value, raw });
      }
      continue;
    }
    const value = unquote(raw);
    if (value) {
      q.text.push(value.toLowerCase());
      q.tokens.push({ kind: "text", value, raw });
    }
  }
  return q;
}

/** Replace (or remove, with value null) the token for a key, keeping everything else as typed. */
export function setToken(input: string, kind: "service" | "level" | "source", value: string | null): string {
  const parts = tokenise(input).filter((raw) => {
    const kv = /^([a-z]+):/i.exec(raw);
    return !(kv && KEY_ALIASES[kv[1]!.toLowerCase()] === kind);
  });
  if (value) parts.unshift(`${kind}:${/\s/.test(value) ? `"${value}"` : value}`);
  return parts.join(" ");
}

/** Remove one token (by its raw text) from the query. */
export function removeToken(input: string, raw: string): string {
  const parts = tokenise(input);
  const i = parts.indexOf(raw);
  if (i >= 0) parts.splice(i, 1);
  return parts.join(" ");
}

/** What we send to the API: it does the heavy filtering; the browser does the rest. */
export function serverFilter(q: ParsedQuery): { service?: string; level?: Level; source?: Exclude<Source, "platform">; q?: string } {
  const longest = [...q.text].sort((a, b) => b.length - a.length)[0];
  return {
    ...(q.service ? { service: q.service } : {}),
    ...(q.level ? { level: q.level } : {}),
    ...(q.source && q.source !== "platform" ? { source: q.source } : {}),
    ...(longest ? { q: longest } : {}),
  };
}

const levelRank = (l: string) => {
  const i = (LEVELS as readonly string[]).indexOf(l);
  return i === -1 ? 1 : i;
};

/** Full client-side match, so live lines and cached lines obey every part of the query. */
export function matchLine(l: LogLine, q: ParsedQuery): boolean {
  if (q.service && l.service?.name !== q.service && l.service?.id !== q.service) return false;
  if (q.level && levelRank(l.level) < levelRank(q.level)) return false;
  if (q.source && l.source !== q.source) return false;
  if (q.status.length) {
    const st = l.http?.status;
    if (st == null || !q.status.every((m) => m.test(st))) return false;
  }
  if (q.path && !(l.http?.path ?? "").startsWith(q.path)) return false;
  if (q.method && (l.http?.method ?? "").toUpperCase() !== q.method) return false;
  if (q.text.length || q.exclude.length) {
    const hay = `${l.message} ${l.http?.path ?? ""} ${l.http?.request_id ?? ""}`.toLowerCase();
    if (!q.text.every((t) => hay.includes(t))) return false;
    if (q.exclude.some((t) => hay.includes(t))) return false;
  }
  return true;
}

/** Equivalent `guv logs` command for Show as code. */
export function cliFor(q: ParsedQuery, env: string, live: boolean, since?: string): string {
  const parts = ["guv logs"];
  if (live) parts.push("-f");
  if (q.service) parts.push(`--service ${q.service}`);
  parts.push(`--env ${env}`);
  if (q.level) parts.push(`--level ${q.level}`);
  if (q.source) parts.push(`--source ${q.source}`);
  if (!live && since) parts.push(`--since ${since}`);
  for (const t of q.text) parts.push(`--grep ${JSON.stringify(t)}`);
  return parts.join(" ");
}

export const EXAMPLES: { query: string; meaning: string }[] = [
  { query: "level:error", meaning: "Errors and anything worse" },
  { query: "service:api status:5xx", meaning: "Failed requests to api" },
  { query: 'path:/api -healthz "timeout"', meaning: "API lines mentioning timeout, without health checks" },
  { query: "source:http status:>=400 method:POST", meaning: "POSTs that did not succeed" },
];
